"use client";

import { useState, useEffect } from "react";
import SparklineFilledGradient from "./SparklineFilledGradient";

interface Props {
  tokenId: string;
  width?: number;
  height?: number;
}

/**
 * Fetches 24h price history for a Polymarket CLOB tokenId and renders a
 * filled-gradient sparkline. Used in the TrendingMarkets sidebar widget
 * and the multi-outcome DegenBox EventTile to give an at-a-glance trend
 * for each outcome. Skips rendering on fetch failure so missing data
 * doesn't leave a stuck skeleton.
 */
export default function MarketSparkline({ tokenId, width = 72, height = 28 }: Props) {
  const [data, setData] = useState<number[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`/api/polymarket/prices-history?tokenId=${tokenId}&interval=1d&fidelity=60`)
      .then((res) => res.json())
      .then((json) => {
        if (json.success && json.data?.history?.length >= 2) {
          setData(json.data.history.map((p: { price: number }) => p.price));
        } else {
          setFailed(true);
        }
      })
      .catch(() => setFailed(true));
  }, [tokenId]);

  if (failed) return <div style={{ width, height }} />;
  if (!data) return <div className="animate-pulse bg-gray-100 dark:bg-[#0f0f12] rounded" style={{ width, height }} />;
  return <SparklineFilledGradient data={data} width={width} height={height} />;
}
